import React from 'react';

import { ValueBox, ValueGroup, ValueSelectInput } from '@candu-ai/craft/ui';
import { BuiltInComponents } from 'craft/constants';
import { EmptyPlaceholder } from 'craft/containers/Toolbox/EmptyPlaceholder';
import { Section } from 'craft/containers/Toolbox/components';
import { useMultiNodes } from 'craft/hooks';
import { useFlowAncestor, useFormAncestor } from 'craft/state';
import { HANDLER_TYPES } from 'craft/types';

interface Props {
  nodeIds: string[];
}

const handlerTypes = [
  {
    value: HANDLER_TYPES.LEGACY_SUBMIT_FORM,
    label: 'Submit',
  },
  {
    value: HANDLER_TYPES.LEGACY_SUBMIT_FORM_AND_SET_STEP,
    label: 'Save and go to Flow Step',
  },
];

type Handler = {
  value: HANDLER_TYPES;
  label: string;
};

export const FormSubmitAction = React.memo(({ nodeIds }: Props) => {
  const { formNodeId } = useFormAncestor(nodeIds[0]);
  const { stepIds, activeStepId, index: stepIndex } = useFlowAncestor(nodeIds[0]);
  const { type } = useMultiNodes(nodeIds);

  const {
    submitGoToFlowStepId,
    actions: { setProps },
  } = useMultiNodes([formNodeId ?? ''], (node) => ({
    submitGoToFlowStepId: node?.data?.props?.submitGoToFlowStepId,
  }));

  if (!submitGoToFlowStepId) {
    // Unless the form is already set to submit and go to next step, don't show these options
    return (
      <EmptyPlaceholder
        title="Static Component"
        subtitle="The Component you selected is not interactive"
      />
    );
  }

  const defaultAction = submitGoToFlowStepId
    ? HANDLER_TYPES.LEGACY_SUBMIT_FORM_AND_SET_STEP
    : HANDLER_TYPES.LEGACY_SUBMIT_FORM;

  const defaultStepIdForNewAction = stepIds[stepIndex === 0 ? 1 : 0];
  const onSetHandler = (selected: Handler | null) => {
    if (selected?.value === HANDLER_TYPES.LEGACY_SUBMIT_FORM_AND_SET_STEP) {
      setProps({ submitGoToFlowStepId: defaultStepIdForNewAction });
      return;
    }
    setProps({ submitGoToFlowStepId: undefined });
  };

  const setStepIdOptions = stepIds
    .map((value, index) => ({
      label: `Step ${index + 1}`,
      value,
    }))
    // you should not be able to link to the active step id...
    .filter(({ value }) => value !== activeStepId);

  const defaultStepId = submitGoToFlowStepId || defaultStepIdForNewAction;

  const onSetStepId = (selected: { value: string; label: string }) => {
    setProps({ submitGoToFlowStepId: selected?.value });
  };

  return (
    <Section
      id="toolbox-interactions"
      name={type === BuiltInComponents.Form ? 'Action on submit' : 'Action on click/tap'}
    >
      <ValueGroup>
        <ValueBox
          isExpandable={{
            isExpanded: defaultAction === HANDLER_TYPES.LEGACY_SUBMIT_FORM_AND_SET_STEP,
          }}
        >
          <ValueSelectInput<Handler>
            clearable={false}
            scale="small"
            items={handlerTypes}
            selectedItem={handlerTypes.find(({ value }) => value === defaultAction)}
            onSelect={onSetHandler}
          />
        </ValueBox>

        {defaultAction === HANDLER_TYPES.LEGACY_SUBMIT_FORM_AND_SET_STEP && (
          <ValueBox isInGroup={{ isLast: true }}>
            <ValueSelectInput
              scale="small"
              clearable={false}
              items={setStepIdOptions}
              selectedItem={setStepIdOptions.find(({ value }) => value === defaultStepId)}
              onSelect={onSetStepId}
            />
          </ValueBox>
        )}
      </ValueGroup>
    </Section>
  );
});
